"use client";

import { motion } from "framer-motion";
import {
  Shield,
  AlertTriangle,
  Briefcase,
  Anchor,
  CheckCircle,
} from "lucide-react";
import SectionHero from "../components/SectionHero";
import ContentSection from "../components/ContentSection";
import { useEffect } from "react";

const ShipInsurance = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const coverTypes = [
    {
      icon: Anchor,
      title: "Hull & Machinery",
      description: "Insurance for physical damage to the vessel and equipment",
    },
    {
      icon: Users,
      title: "Liability Covers",
      description:
        "Coverage relating to crew, cargo, and third-party liabilities",
    },
    {
      icon: Briefcase,
      title: "Trade-Specific Covers",
      description: "Additional covers for specific trades or high-risk regions",
    },
    {
      icon: AlertTriangle,
      title: "Specialist Covers",
      description: "Customised coverage for offshore and high-risk operations",
    },
  ];

  const support = [
    {
      title: "Risk Assessment",
      description:
        "Reviewing vessel and trading details to understand risk exposure and insurance needs.",
    },
    {
      title: "Intermediary Coordination",
      description:
        "Working with insurance intermediaries and underwriters to secure appropriate coverage.",
    },
    {
      title: "Documentation Support",
      description:
        "Ensuring that all documentation and surveys required by insurers are current and complete.",
    },
    {
      title: "Claims Assistance",
      description:
        "Supporting claim preparation and communication when an incident occurs.",
    },
    {
      title: "Requirement Updates",
      description:
        "Helping owners understand changes in requirements and premium drivers.",
    },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Ship Insurance"
        subtitle="Protecting Against the Risks You Cannot See"
        description="Shipping carries technical, operational, commercial and legal risks. We help owners understand and manage their marine insurance requirements."
      />

      <ContentSection title="Types of Marine Covers">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {coverTypes.map((cover, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="rounded-xl bg-slate-50 p-6 text-center shadow-lg"
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                <cover.icon className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="mb-2 font-bold text-slate-900">{cover.title}</h3>
              <p className="text-sm text-slate-600">{cover.description}</p>
            </motion.div>
          ))}
        </div>
      </ContentSection>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              How We Assist
            </h2>
            <p className="mx-auto max-w-3xl text-slate-600">
              We keep the process transparent so that insurance is seen as a
              planned cost, not a surprise.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
            {support.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-white p-6 shadow-lg"
              >
                <div className="mb-4 h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <span className="text-lg font-bold text-teal-600">
                    {index + 1}
                  </span>
                </div>
                <h3 className="mb-2 font-bold text-slate-900">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">
                Transparent Insurance Planning
              </h2>
              <p className="mb-6 text-slate-700">
                Insurance does not remove shipping risks, but it can protect
                owners from the financial impact when something goes wrong. Our
                role is to help you discuss your needs with suitable providers
                and ensure that coverage matches your actual trading pattern.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Comprehensive risk evaluation
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Customised coverage recommendations
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Proactive premium management
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Incident support and guidance
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl bg-gradient-to-br from-teal-600 to-teal-700 p-8 text-white"
            >
              <Shield className="mb-4 h-12 w-12" />
              <h3 className="mb-4 text-2xl font-bold">Your Coverage Matters</h3>
              <p className="mb-6 leading-relaxed">
                From vessel valuations to operational risks, from crew liability
                to cargo concerns – we ensure you have the right protection for
                your shipping operation.
              </p>
              <button className="rounded-lg bg-white px-6 py-2 font-semibold text-teal-600 transition-colors duration-300 hover:bg-teal-50">
                Get Coverage Assessment
              </button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShipInsurance;

// Note: Added mock import for Users icon, replace with actual import if needed
const Users = AlertTriangle;
